// src/components/SidebarLayout.js
import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'

function SidebarLayout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [userEmail, setUserEmail] = useState(null)
  const [userName, setUserName] = useState(null)
  const [userRole, setUserRole] = useState(null)

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.user) {
        setUserEmail(null)
        setUserName(null)
        setUserRole(null)
        return
      }

      const authUser = session.user
      setUserEmail(authUser.email || null)

      const { data: appUser, error } = await supabase
        .from('users')
        .select('full_name, role')
        .eq('id', authUser.id)
        .single()

      if (!error && appUser) {
        setUserName(appUser.full_name || null)
        setUserRole(appUser.role || null)
      }
    }

    fetchUser()
  }, [])

  const isDemoUser = userEmail === 'demo@cuvriq.com'

  const baseNavItems = isDemoUser
    ? [
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Ask Riq', path: '/chat' },
      ]
    : [
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Ask Riq', path: '/chat' },
        { label: 'Upload', path: '/upload' },
        { label: 'View all Plans', path: '/plans-by-group' },
        { label: 'Feedback', path: '/Feedback' },
        { label: 'FAQ', path: '/faq' },
      ]

  const navItems =
    userRole === 'admin'
      ? [...baseNavItems, { label: 'User Approvals', path: '/admin/users' }]
      : baseNavItems

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const isActive = (path) => location.pathname === path

  const displayName =
    userName ||
    (userEmail ? userEmail.split('@')[0] : null)

  const displayRole = userRole ? userRole.toLowerCase() : null

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: '220px',
          background: '#1e293b',
          color: '#fff',
          padding: '1.5rem 1rem',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* Top Section */}
        <div>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <Link to="/dashboard" style={{ display: 'inline-block' }}>
              <img
                src={'/cuvriq-logo-nobg.png'}
                alt="CuvrIQ Logo"
                style={{
                  width: '120px',
                  height: 'auto',
                  marginBottom: '0.5rem',
                  borderRadius: '8px',
                }}
              />
            </Link>
          </div>

          {/* Demo Tag */}
          {isDemoUser && (
            <div
              style={{
                marginBottom: '1.5rem',
                backgroundColor: '#facc15',
                color: '#1e293b',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              DEMO USER - Restricted Access
              <span
                title="This demo account is limited to Ask Riq and Dashboard only. Upload and editing is disabled."
                style={{
                  display: 'inline-block',
                  fontSize: '0.9rem',
                  cursor: 'help',
                  color: '#1e293b',
                }}
              >
                ⓘ
              </span>
            </div>
          )}

          {/* Navigation */}
          <nav>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {navItems.map((item) => (
                <li key={item.path} style={{ marginBottom: '1rem' }}>
                  <Link
                    to={item.path}
                    style={{
                      display: 'block',
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      backgroundColor: isActive(item.path)
                        ? '#38bdf8'
                        : 'transparent',
                      color: isActive(item.path) ? '#1e293b' : '#cbd5e1',
                      textDecoration: 'none',
                      fontWeight: isActive(item.path) ? 'bold' : 'normal',
                      transition: 'all 0.2s ease-in-out',
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = '#334155')
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.backgroundColor = isActive(item.path)
                        ? '#38bdf8'
                        : 'transparent')
                    }
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Bottom Section */}
        <div
          style={{
            borderTop: '1px solid #334155',
            paddingTop: '0.75rem',
            marginTop: '0.75rem',
          }}
        >
         
          {/* Legal Links */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.4rem',
              marginBottom: '0.75rem',
            }}
          >
            <Link
              to="/terms"
              style={{
                fontSize: '0.75rem',
                color: '#94a3b8',
                textDecoration: 'none',
              }}
            >
              Terms of Service
            </Link>

            <Link
              to="/privacy"
              style={{
                fontSize: '0.75rem',
                color: '#94a3b8',
                textDecoration: 'none',
              }}
            >
              Privacy Policy
            </Link>

            <Link
              to="/disclaimer"
              style={{
                fontSize: '0.75rem',
                color: '#94a3b8',
                textDecoration: 'none',
              }}
            >
              Disclaimer
            </Link>
          </div>

          {/* Divider */}
          <hr style={{ borderColor: '#334155', margin: '0.75rem 0' }} />

          {/* User Info */}
          {displayName && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.6rem',
                marginBottom: '0.75rem',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '999px',
                  backgroundColor: '#0f172a',
                  border: '1px solid #38bdf8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                }}
              >
                {displayName.charAt(0)}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#e5e7eb',
                  }}
                >
                  {displayName}
                </span>

                {displayRole && (
                  <span
                    style={{
                      fontSize: '0.7rem',
                      color: '#9ca3af',
                      textTransform: 'capitalize',
                    }}
                  >
                    {displayRole}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Logout */}
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              backgroundColor: '#ef4444',
              color: '#fff',
              border: 'none',
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#dc2626')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = '#ef4444')}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Page Content */}
      <div
        style={{
          flex: 1,
          background: '#f9fafb',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default SidebarLayout
